import { inject, provide, ref, toValue, watch } from 'vue'
import { api, endpoints } from '../../services/api.js'
import { useToast } from '../useToast.js'

/**
 * DOCUMENTS JOINTS AUX RÉPONSES du questionnaire de qualification, côté écran.
 *
 * L'écran qui connaît la demande (tunnel du commercial, questionnaire du
 * client) FOURNIT le contexte ; le rendu d'une question, plus bas, l'INJECTE.
 * Sans contexte fourni — la demande de devis, où la demande n'existe pas
 * encore —, la zone de dépôt ne s'affiche pas.
 *
 * Les fichiers partent dès qu'on les dépose : ils ne dépendent pas du bouton
 * « Enregistrer » du questionnaire.
 */
const CLE = Symbol('documentsReponses')

/**
 * @param {object} acces
 * @param {() => Promise<object[]>} acces.lister
 * @param {(questionId: number, reponse: string|null, fichiers: File[]) => Promise<object[]>} acces.deposer
 * @param {(document: object) => Promise<string>} acces.lien l'URL signée de téléchargement
 * @param {(document: object) => Promise<void>} acces.retirer
 * @param {(document: object) => boolean} [acces.peutRetirer]
 * @param {*} [declencheur] ref/getter : recharger la liste quand il change (id de demande, jeton)
 */
export function fournirDocumentsReponses(acces, declencheur = null) {
  const { showToast } = useToast()
  const documents = ref([])
  const envoiEnCours = ref(null)

  async function charger() {
    try {
      documents.value = await acces.lister()
    } catch {
      documents.value = []
    }
  }

  /** Clé d'un dépôt : la question et la réponse qui l'ouvre. */
  const cleDepot = (questionId, reponse) => `${questionId}:${reponse ?? ''}`

  function documentsDe(questionId, reponse = null) {
    return documents.value.filter((d) => d.questionId === questionId && (d.reponse ?? null) === (reponse ?? null))
  }

  async function deposer(questionId, reponse, fichiers) {
    if (!fichiers?.length) return
    envoiEnCours.value = cleDepot(questionId, reponse)
    try {
      const nouveaux = await acces.deposer(questionId, reponse, fichiers)
      documents.value = [...documents.value, ...(nouveaux || [])]
      showToast(fichiers.length > 1 ? `${fichiers.length} documents ajoutés` : 'Document ajouté', 'success')
    } catch (error) {
      showToast(error.message || 'Le document n’a pas pu être ajouté', 'error')
    } finally {
      envoiEnCours.value = null
    }
  }

  async function telecharger(document) {
    try {
      const url = await acces.lien(document)
      const lien = window.document.createElement('a')
      lien.href = url
      lien.download = document.nomFichier
      lien.rel = 'noopener'
      lien.click()
    } catch (error) {
      showToast(error.message || 'Le document n’a pas pu être téléchargé', 'error')
    }
  }

  async function retirer(document) {
    try {
      await acces.retirer(document)
      documents.value = documents.value.filter((d) => d.id !== document.id)
      showToast('Document retiré', 'success')
    } catch (error) {
      showToast(error.message || 'Le document n’a pas pu être retiré', 'error')
    }
  }

  const contexte = {
    documents,
    envoiEnCours,
    cleDepot,
    documentsDe,
    charger,
    deposer,
    telecharger,
    retirer,
    peutRetirer: acces.peutRetirer || (() => true),
  }

  if (declencheur) watch(() => toValue(declencheur), (valeur) => { if (valeur) charger() }, { immediate: true })
  provide(CLE, contexte)
  return contexte
}

/** Le contexte fourni par l'écran, ou `null` (pas de dépôt possible ici). */
export function useDocumentsReponses() {
  return inject(CLE, null)
}

/** Le formulaire d'un dépôt : la question, la réponse qui l'ouvre, les fichiers. */
function formulaireDeDepot(questionId, reponse, fichiers, champs = {}) {
  const donnees = new FormData()
  for (const [cle, valeur] of Object.entries(champs)) donnees.append(cle, valeur)
  donnees.append('questionId', String(questionId))
  if (reponse) donnees.append('reponse', reponse)
  for (const fichier of fichiers) donnees.append('fichiers', fichier)
  return donnees
}

/** Les accès de l'équipe, par la session, sur une demande. */
export function accesEquipe(quoteId) {
  const id = () => toValue(quoteId)
  return {
    lister: async () => (await api.get(endpoints.quotes.reponseDocuments(id())))?.documents || [],
    deposer: async (questionId, reponse, fichiers) => (
      (await api.post(endpoints.quotes.reponseDocuments(id()), formulaireDeDepot(questionId, reponse, fichiers)))?.documents || []
    ),
    lien: async (document) => (await api.get(endpoints.quotes.downloadReponseDocument(id(), document.id)))?.url,
    retirer: (document) => api.delete(endpoints.quotes.deleteReponseDocument(id(), document.id)),
  }
}

/** Les accès du client, par le jeton de son questionnaire d'organisation. */
export function accesClient(token) {
  const jeton = () => toValue(token)
  const publics = endpoints.questionnaireOrganisation.public
  return {
    lister: async () => (await api.get(publics.reponseDocuments(jeton())))?.documents || [],
    deposer: async (questionId, reponse, fichiers) => (
      (await api.post(publics.uploadReponseDocuments(), formulaireDeDepot(questionId, reponse, fichiers, { token: jeton() })))?.documents || []
    ),
    lien: async (document) => (await api.get(publics.downloadReponseDocument(jeton(), document.id)))?.url,
    retirer: (document) => api.post(publics.deleteReponseDocument(), { token: jeton(), documentId: document.id }),
    // Le client ne retire que ce qu'il a déposé (le serveur le vérifie aussi).
    peutRetirer: (document) => document.source === 'client',
  }
}
